import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Breadcrumb, Button, Select, Tag, message, Modal, Spin } from 'antd';
import { PlusOutlined, LockOutlined, TeamOutlined } from '@ant-design/icons';
import HeaderComponent from '../../Shared/components/HeaderComponent';
import QuincenaTable from '../components/QuincenaTable';
import TrabajadoresModal from '../modals/TrabajadoresModal';
import NuevaQuincenaModal from '../modals/NuevaQuincenaModal';
import useCustomNavigate from '../../../hooks/useCustomNavigate';
import axios from 'axios';

const { Content } = Layout;
const { Option } = Select;

const QuincenasLayout = () => {
    const navigate = useCustomNavigate();
    const [loading, setLoading] = useState(true);
    const [quincenas, setQuincenas] = useState([]);
    const [quincenaSeleccionada, setQuincenaSeleccionada] = useState(null);
    const [quincenaData, setQuincenaData] = useState(null);

    // Modals
    const [openTrabajadoresModal, setOpenTrabajadoresModal] = useState(false);
    const [openNuevaQuincenaModal, setOpenNuevaQuincenaModal] = useState(false);

    const token = localStorage.getItem('token');
    const config = {
        headers: { Authorization: `Bearer ${token}` }
    };

    // Cargar lista de quincenas
    const cargarQuincenas = useCallback(async () => {
        try {
            const response = await axios.get('/api/v1/quincenas', config);
            setQuincenas(response.data);

            // Si hay quincenas, seleccionar la primera (más reciente)
            if (response.data.length > 0 && !quincenaSeleccionada) {
                setQuincenaSeleccionada(response.data[0].id);
            }
        } catch (error) {
            console.error('Error al cargar quincenas:', error);
            message.error('Error al cargar las quincenas');
        }
    }, []);

    // Cargar datos de la quincena seleccionada
    const cargarQuincenaData = useCallback(async () => {
        if (!quincenaSeleccionada) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`/api/v1/quincenas/${quincenaSeleccionada}`, config);
            setQuincenaData(response.data);
        } catch (error) {
            console.error('Error al cargar datos de quincena:', error);
            message.error('Error al cargar los datos de la quincena');
        } finally {
            setLoading(false);
        }
    }, [quincenaSeleccionada]);

    useEffect(() => {
        cargarQuincenas();
    }, []);

    useEffect(() => {
        if (quincenaSeleccionada) {
            cargarQuincenaData();
        }
    }, [quincenaSeleccionada]);

    // Cerrar quincena
    const handleCerrarQuincena = () => {
        Modal.confirm({
            title: '¿Cerrar esta quincena?',
            content: 'Una vez cerrada, no podrás editar los datos. ¿Estás seguro?',
            okText: 'Sí, cerrar',
            cancelText: 'Cancelar',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await axios.put(`/api/v1/quincenas/${quincenaSeleccionada}/cerrar`, {}, config);
                    message.success('Quincena cerrada exitosamente');

                    const obrasAfectadas = response.data.obras_afectadas || [];

                    if (obrasAfectadas.length === 1) {
                        // Si solo hay una obra, navegar directamente
                        const obra = obrasAfectadas[0];
                        navigate('/relationships', {
                            state: {
                                from: {
                                    id: obra.id,
                                    nombre_obra: obra.nombre_obra,
                                    fecha_creacion: obra.fecha_creacion
                                }
                            }
                        });
                    } else if (obrasAfectadas.length > 1) {
                        // Si hay múltiples obras, mostrar modal de selección
                        Modal.info({
                            title: 'Selecciona una obra para ver sus relaciones',
                            content: (
                                <div style={{ marginTop: 16 }}>
                                    {obrasAfectadas.map(obra => (
                                        <Button
                                            key={obra.id}
                                            block
                                            style={{ marginBottom: 8 }}
                                            onClick={() => {
                                                Modal.destroyAll();
                                                navigate('/relationships', {
                                                    state: {
                                                        from: {
                                                            id: obra.id,
                                                            nombre_obra: obra.nombre_obra,
                                                            fecha_creacion: obra.fecha_creacion
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            {obra.nombre_obra}
                                        </Button>
                                    ))}
                                </div>
                            ),
                            okText: 'Cancelar',
                            onOk: () => {
                                cargarQuincenas();
                                cargarQuincenaData();
                            }
                        });
                    } else {
                        // No hay obras afectadas, solo recargar
                        cargarQuincenas();
                        cargarQuincenaData();
                    }
                } catch (error) {
                    console.error('Error al cerrar quincena:', error);
                    message.error('Error al cerrar la quincena');
                }
            }
        });
    };

    // Actualizar pago de un trabajador
    const handleUpdatePago = async (trabajadorId, datos) => {
        try {
            await axios.put(
                `/api/v1/quincenas/${quincenaSeleccionada}/trabajador/${trabajadorId}`,
                datos,
                config
            );
            cargarQuincenaData();
        } catch (error) {
            console.error('Error al actualizar pago:', error);
            message.error('Error al actualizar el pago');
        }
    };

    // Callbacks de modales
    const handleNuevaQuincenaCreada = () => {
        setOpenNuevaQuincenaModal(false);
        cargarQuincenas();
    };

    const handleTrabajadoresActualizados = () => {
        cargarQuincenaData();
    };

    const quincenaActual = quincenas.find(q => q.id === quincenaSeleccionada);
    const esCerrada = quincenaActual?.cerrada;

    return (
        <Layout>
            <Content>
                <Breadcrumb
                    className='breadcrumb'
                    items={[
                        { title: <a href="/home">Inicio</a> },
                        { title: "Quincenas" }
                    ]}
                />

                <HeaderComponent title="Quincenas" />

                {/* Toolbar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    flexWrap: 'wrap',
                    gap: 12
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Select
                            style={{ width: 300 }}
                            placeholder="Seleccionar quincena"
                            value={quincenaSeleccionada}
                            onChange={setQuincenaSeleccionada}
                        >
                            {quincenas.map(q => (
                                <Option key={q.id} value={q.id}>
                                    {q.nombre} {q.cerrada && <LockOutlined />}
                                </Option>
                            ))}
                        </Select>

                        {esCerrada && (
                            <Tag color="red" icon={<LockOutlined />}>
                                Cerrada
                            </Tag>
                        )}
                        {!esCerrada && quincenaActual && (
                            <Tag color="green">
                                Abierta
                            </Tag>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            icon={<TeamOutlined />}
                            onClick={() => setOpenTrabajadoresModal(true)}
                        >
                            Gestionar Trabajadores
                        </Button>

                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setOpenNuevaQuincenaModal(true)}
                        >
                            Nueva Quincena
                        </Button>

                        {!esCerrada && quincenaActual && (
                            <Button
                                danger
                                icon={<LockOutlined />}
                                onClick={handleCerrarQuincena}
                            >
                                Cerrar Quincena
                            </Button>
                        )}
                    </div>
                </div>

                {/* Tabla de Quincena */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 50 }}>
                        <Spin size="large" />
                    </div>
                ) : quincenaData ? (
                    <QuincenaTable
                        quincenaData={quincenaData}
                        onUpdatePago={handleUpdatePago}
                        disabled={esCerrada}
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: 50 }}>
                        <p>No hay quincenas creadas. Crea una nueva para comenzar.</p>
                    </div>
                )}

                {/* Modales */}
                <TrabajadoresModal
                    open={openTrabajadoresModal}
                    onClose={() => setOpenTrabajadoresModal(false)}
                    onUpdate={handleTrabajadoresActualizados}
                />

                <NuevaQuincenaModal
                    open={openNuevaQuincenaModal}
                    onClose={() => setOpenNuevaQuincenaModal(false)}
                    onCreated={handleNuevaQuincenaCreada}
                />
            </Content>
        </Layout>
    );
};

export default QuincenasLayout;
